import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { XMarkIcon, CalendarIcon, UserIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { payrollService } from '../../../services/accounting/payroll';
import { formatCurrency } from '../../../utils/formatting';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../ui/LoadingSpinner';

/**
 * CreatePayrollPeriodModal Component
 * 
 * Modal for creating new payroll periods
 */
const CreatePayrollPeriodModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  const { isRTL } = useTheme();
  
  // Fallback RTL detection
  const isArabic = t('common.language_code', 'en') === 'ar';
  const shouldUseRTL = isRTL || isArabic;
  
  // Debug RTL detection
  console.log('🔍 RTL Detection:', { isRTL, isArabic, shouldUseRTL, language: t('common.language_code', 'en') });
  
  // Force RTL for Arabic language
  const forceRTL = t('common.language_code', 'en') === 'ar';
  
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [includeCommissions, setIncludeCommissions] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  const [employeeCommissions, setEmployeeCommissions] = useState({});
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });

  const loadEmployees = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      setLoadingEmployees(true);
      console.log('🔄 Loading employees for company:', currentCompany.id);
      const employeesData = await payrollService.getEmployees(currentCompany.id);
      console.log('👥 Employees loaded:', employeesData.length);
      console.log('💰 Employee salaries:', employeesData.map(emp => ({ name: emp.name, salary: emp.salary })));
      
      setEmployees(employeesData);
      // Select only active employees by default
      const activeEmployeeIds = employeesData
        .filter(emp => emp.status === 'active')
        .map(emp => emp.id);
      setSelectedEmployees(activeEmployeeIds);
      console.log('✅ Selected active employees:', activeEmployeeIds.length, 'out of', employeesData.length, 'total');
    } catch (error) {
      console.error('❌ Error loading employees:', error);
      toast.error(t('payroll.error.loading_employees', 'Failed to load employees'));
    } finally {
      setLoadingEmployees(false);
    }
  }, [currentCompany?.id, t]);

  const calculateTotalAmount = useCallback(async () => {
    if (!currentCompany?.id || selectedEmployees.length === 0) {
      console.log('🔍 No company or employees selected, setting total to 0');
      setTotalAmount(0);
      setEmployeeCommissions({});
      return;
    }

    try {
      const startDate = new Date(formData.year, formData.month - 1, 1);
      const endDate = new Date(formData.year, formData.month, 0);
      console.log('🔍 Calculating total for period:', { 
        startDate, 
        endDate, 
        selectedCount: selectedEmployees.length,
        includeCommissions 
      });
      
      let total = 0;
      const commissions = {};
      
      for (const employeeId of selectedEmployees) {
        const employee = employees.find(emp => emp.id === employeeId);
        if (!employee) {
          console.log('⚠️ Employee not found:', employeeId);
          continue;
        }
        
        console.log('💰 Processing employee:', employee.name, 'Salary:', employee.salary);
        
        // Add base salary
        const salary = parseFloat(employee.salary) || 0;
        total += salary;
        console.log('💰 Added salary:', salary, 'Running total:', total);
        
        // Calculate and store commissions for all eligible employees (for display)
        if (employee.type === 'driver' && employee.commissionEligible) {
          try {
            console.log('🔍 Calculating commissions for driver:', employee.name);
            const commissionData = await payrollService.calculateDriverCommissions(
              employeeId, 
              currentCompany.id, 
              startDate, 
              endDate
            );
            const commissionAmount = commissionData.unpaidCommissions || 0;
            commissions[employeeId] = commissionAmount;
            
            // Only add to total if commissions are included
            if (includeCommissions) {
              total += commissionAmount;
              console.log('💰 Added commission:', commissionAmount, 'Running total:', total);
            } else {
              console.log('💰 Commission calculated but not included in total:', commissionAmount);
            }
          } catch (error) {
            console.error('❌ Error calculating commissions for employee:', employeeId, error);
            commissions[employeeId] = 0;
          }
        } else {
          commissions[employeeId] = 0;
        }
      }
      
      console.log('✅ Final total calculated:', total);
      console.log('💰 Commission amounts:', commissions);
      console.log('🔧 Include commissions setting:', includeCommissions);
      setTotalAmount(total);
      setEmployeeCommissions(commissions);
    } catch (error) {
      console.error('❌ Error calculating total amount:', error);
    }
  }, [currentCompany?.id, selectedEmployees, employees, includeCommissions, formData]);

  // Load employees when modal opens
  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadEmployees();
    }
  }, [isOpen, currentCompany?.id, loadEmployees]);

  // Calculate totals when selection changes
  useEffect(() => {
    calculateTotalAmount();
  }, [selectedEmployees, includeCommissions, formData, calculateTotalAmount]);

  const handleEmployeeToggle = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    
    // Allow selecting/deselecting any employee, but warn about inactive ones
    if (employee && employee.status !== 'active') {
      const isCurrentlySelected = selectedEmployees.includes(employeeId);
      if (!isCurrentlySelected) {
        toast.warning(t('payroll.warning_inactive_employee', 'Warning: This employee is inactive'));
      }
    }
    
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    // Only select active employees
    const activeEmployeeIds = employees
      .filter(emp => emp.status === 'active')
      .map(emp => emp.id);
    setSelectedEmployees(activeEmployeeIds);
  };

  const handleDeselectAll = () => {
    setSelectedEmployees([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentCompany?.id) return;
    
    if (selectedEmployees.length === 0) {
      toast.error(t('payroll.error.no_employees_selected', 'Please select at least one employee'));
      return;
    }
    
    try {
      setLoading(true);
      
      await payrollService.createPayrollPeriod(
        currentCompany.id,
        formData.year,
        formData.month,
        currentUser?.uid || 'system'
      );
      
      toast.success(t('payroll.success.period_created', 'Payroll period created successfully'));
      onSuccess();
      
    } catch (error) {
      console.error('Error creating payroll period:', error);
      toast.error(t('payroll.error.period_creation_failed', 'Failed to create payroll period'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        dir={forceRTL ? 'rtl' : 'ltr'}
        style={{ direction: forceRTL ? 'rtl' : 'ltr', textAlign: forceRTL ? 'right' : 'left' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('payroll.create_period', 'Create Payroll Period')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('payroll.year', 'Year')}
              </label>
              <select
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                className="form-select"
                required
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('payroll.month', 'Month')}
              </label>
              <select
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                className="form-select"
                required
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const month = i + 1;
                  const monthName = new Date(2024, i, 1).toLocaleDateString('en', { month: 'long' });
                  return (
                    <option key={month} value={month}>
                      {month.toString().padStart(2, '0')} - {monthName}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Commission Option */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="includeCommissions"
              checked={includeCommissions}
              onChange={(e) => setIncludeCommissions(e.target.checked)}
              className="form-checkbox h-4 w-4 text-blue-600"
            />
            <label htmlFor="includeCommissions" className="text-sm font-medium text-gray-700">
              {t('payroll.include_commissions', 'Include Commissions')}
            </label>
          </div>

          {/* Total Amount Display */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CurrencyDollarIcon className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-900">
                  {t('payroll.total_amount_owed', 'Total Amount Owed')}:
                </span>
              </div>
              <span className="text-lg font-bold text-green-900">
                {formatCurrency(totalAmount)}
              </span>
            </div>
            <div className="mt-2 text-xs text-green-700">
              {t('payroll.selected_employees', 'Selected')}: {selectedEmployees.length} / {employees.filter(emp => emp.status === 'active').length} {t('payroll.employees', 'employees')}
            </div>
          </div>

          {/* Employee Selection */}
          <div style={{ direction: forceRTL ? 'rtl' : 'ltr' }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 flex items-center">
                <UserIcon className="w-4 h-4 mr-2" />
                {t('payroll.select_employees', 'Select Employees')}
              </h4>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  {t('payroll.select_all', 'Select All Active')}
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  {t('payroll.deselect_all', 'Deselect All')}
                </button>
              </div>
            </div>

            {loadingEmployees ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="md" />
                <span className="ml-2 text-sm text-gray-600">
                  {t('payroll.loading_employees', 'Loading employees...')}
                </span>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                {employees.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {t('payroll.no_employees', 'No employees found')}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {employees.map((employee) => {
                      const isActive = employee.status === 'active';
                      const commissionAmount = employeeCommissions[employee.id] || 0;
                      
                      return (
                        <div 
                          key={employee.id} 
                          className={`p-3 transition-colors ${
                            isActive ? 'hover:bg-gray-50' : 'bg-gray-100 opacity-60'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id={`employee-${employee.id}`}
                              checked={selectedEmployees.includes(employee.id)}
                              onChange={() => handleEmployeeToggle(employee.id)}
                              disabled={!isActive}
                              className={`form-checkbox h-4 w-4 text-blue-600 ${!isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <label 
                                  htmlFor={`employee-${employee.id}`} 
                                  className={`text-sm font-medium cursor-pointer ${
                                    isActive ? 'text-gray-900' : 'text-gray-500'
                                  }`}
                                >
                                  {employee.name}
                                </label>
                                {!isActive && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {t('common.inactive', t('common.inactive'))}
                                  </span>
                                )}
                                {employee.type === 'driver' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {t('payroll.driver', 'Driver')}
                                  </span>
                                )}
                              </div>
                              <div className={`text-xs ${isActive ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
                                <div className="flex flex-wrap gap-2">
                                  <span>{employee.role}</span>
                                  <span>•</span>
                                  <span>{t('payroll.salary', 'Salary')}: {formatCurrency(employee.salary || 0)}</span>
                                  {employee.commissionEligible && (
                                    <>
                                      <span>•</span>
                                      <span className="text-blue-600">
                                        {t('payroll.commission_eligible', 'Commission Eligible')}
                                      </span>
                                      {commissionAmount > 0 && (
                                        <>
                                          <span>•</span>
                                          <span className={`font-medium ${includeCommissions ? 'text-purple-600' : 'text-gray-400'}`}>
                                            {t('payroll.commission', 'Commission')}: {formatCurrency(commissionAmount)}
                                            {!includeCommissions && (
                                              <span className="text-xs text-gray-500 ml-1">
                                                ({t('payroll.not_included', 'not included')})
                                              </span>
                                            )}
                                          </span>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Period Information */}
          <div 
            className="bg-blue-50 border border-blue-200 rounded-lg p-4"
            style={{ direction: forceRTL ? 'rtl' : 'ltr', textAlign: forceRTL ? 'right' : 'left' }}
          >
            <div className="flex items-start">
              <CalendarIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">
                  {t('payroll.period_info', 'Period Information')}
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  {t('payroll.period_description', 'This will create a new payroll period for processing employee salaries and commissions.')}
                </p>
                <div className="mt-2 text-sm text-blue-700">
                  <strong>{t('payroll.period_dates', 'Period Dates')}:</strong> {' '}
                  {new Date(formData.year, formData.month - 1, 1).toLocaleDateString()} - {' '}
                  {new Date(formData.year, formData.month, 0).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              {t('common.cancel', t('common.cancel'))}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.creating', 'Creating...')}
                </>
              ) : (
                <>
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {t('payroll.create_period', 'Create Period')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePayrollPeriodModal;